import {
  contextKey,
  type DomainCommandContext,
  DomainFault,
  type DomainModule,
  type DomainReadContext,
  paginate,
  type QueryInput,
  type SearchDocument,
} from '../../contracts';
import type { Entity, Kind, Role } from '../../types';
import { recommendation } from '../../workspace';

const roles: Role[] = [
  'groupLeader',
  'groupOperator',
  'regionManager',
  'campusManager',
  'researchLead',
  'studentService',
  'teacher',
  'reviewer',
  'deviceManager',
  'systemAdmin',
  'support',
];
const managers: Role[] = ['campusManager', 'groupOperator', 'regionManager'];
const unfinished = (row: Entity) =>
  !['completed', 'closed', 'cancelled'].includes(row.status);
const need = (text: string | undefined, label: string) => {
  if (!text?.trim()) throw new DomainFault('VALIDATION', `请填写${label}`);
  return text.trim();
};
export function dependencies(row: Entity): string[] {
  try {
    const ids: unknown = JSON.parse(row.fields?.dependsOn || '[]');
    if (!Array.isArray(ids) || ids.some((x) => typeof x !== 'string'))
      throw Error();
    return [...new Set(ids)] as string[];
  } catch {
    throw new DomainFault('VALIDATION', '任务依赖记录无效，请核实');
  }
}
function task(ctx: DomainReadContext, uuid?: string) {
  const row = ctx.get(uuid);
  if (!row || row.kind !== 'task')
    throw new DomainFault('FORBIDDEN', '任务不可访问');
  return row;
}
function actor(ctx: DomainReadContext, row: Entity) {
  if (
    !managers.includes(ctx.context.role) &&
    row.owner !== ctx.context.viewMemberUuid
  )
    throw new DomainFault(
      'FORBIDDEN',
      '只有负责人或组织管理人员可以处理此任务',
    );
}
function transition(row: Entity, states: string[]) {
  if (!states.includes(row.status))
    throw new DomainFault('INVALID_TRANSITION', '任务状态已变化，请刷新后重试');
}
function requireDependencies(ctx: DomainReadContext, row: Entity) {
  if (
    dependencies(row).some((uuid) => {
      const dep = ctx.get(uuid);
      return !dep || unfinished(dep);
    })
  )
    throw new DomainFault(
      'INVALID_TRANSITION',
      '前置任务未完成或不可访问，请先发起协调',
    );
}
function record(ctx: DomainCommandContext, row: Entity, label: string) {
  ctx.touch(row);
  ctx.notify(row, label);
}
export function visibleTasks(ctx: DomainReadContext) {
  return ctx
    .list('task')
    .sort(
      (a, b) =>
        (a.dueAt || '9999').localeCompare(b.dueAt || '9999') ||
        a.uuid.localeCompare(b.uuid),
    );
}
export function messageGroups(ctx: DomainReadContext) {
  const groups = new Map<
    string,
    { source: Entity | undefined; messages: Entity[]; key: string }
  >();
  for (const row of ctx
    .list('message')
    .filter((e) => e.fields?.systemPreference !== 'true')) {
    const source = ctx.get(row.sourceUuid);
    const key = row.sourceUuid || row.uuid;
    const group = groups.get(key) || { source, messages: [], key };
    group.messages.push(row);
    groups.set(key, group);
  }
  return [...groups.values()]
    .reverse()
    .map((g) => ({
      ...g,
      messages: g.messages
        .reverse()
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
      unread: g.messages.filter((m) => m.status === 'unread').length,
    }))
    .sort((a, b) =>
      b.messages[0].createdAt.localeCompare(a.messages[0].createdAt),
    );
}
const terms: Record<string, string[]> = {
  承诺: ['承诺', '答应', '本周五', '反馈', '安排'],
  教学: ['课堂', '提问', '教学', '回应', '复盘'],
  设备: ['工牌', '设备', '采集'],
  案例: ['案例', '方法', '实践'],
  任务: ['任务', '处理', '核实'],
};
export function expandTerms(keyword: string) {
  return [
    ...new Set(
      keyword
        .trim()
        .split(/\s+/)
        .flatMap((word) => terms[word] || [word]),
    ),
  ];
}
const searchable: Kind[] = [
  'recording',
  'device',
  'clue',
  'case',
  'task',
  'knowledge',
  'plan',
  'followUp',
  'repair',
  'batch',
  'teacher',
  'lesson',
  'student',
];
export function workDocuments(ctx: DomainReadContext): SearchDocument[] {
  return searchable.flatMap((kind) =>
    ctx.list(kind).flatMap((row) => {
      if (row.kind === 'recording') {
        const version = row.versions?.at(-1);
        if (version?.segments.length)
          return version.segments.map((seg) => ({
            id: `${row.uuid}:${version.uuid}:${seg.uuid}`,
            entityUuid: row.uuid,
            title: row.title,
            text: `${row.title} ${seg.text}`,
            kind: row.kind,
            memberUuid: row.memberUuid,
            occurredAt: row.startAt || row.createdAt,
            evidence: {
              recordingUuid: row.uuid,
              transcriptVersionUuid: version.uuid,
              startMs: seg.startMs,
              endMs: seg.endMs,
              quote: seg.text,
            },
          }));
      }
      return [
        {
          id: row.uuid,
          entityUuid: row.uuid,
          title: row.title,
          text: `${row.title} ${row.note} ${row.result || ''}`,
          kind: row.kind,
          memberUuid: row.memberUuid,
          occurredAt: row.createdAt,
          evidence: row.evidence,
        },
      ];
    }),
  );
}
export function workQuery(ctx: DomainReadContext, input: QueryInput) {
  let rows = visibleTasks(ctx);
  if (input.status) rows = rows.filter((e) => e.status === input.status);
  if (input.keyword)
    rows = rows.filter((e) => e.title.includes(input.keyword || ''));
  if (input.memberUuid) rows = rows.filter((e) => e.owner === input.memberUuid);
  if (input.from)
    rows = rows.filter(
      (e) => Date.parse(e.createdAt) >= Date.parse(input.from || ''),
    );
  if (input.to)
    rows = rows.filter(
      (e) => Date.parse(e.createdAt) <= Date.parse(input.to || ''),
    );
  if (input.sort)
    rows.sort(
      (a, b) =>
        String(a[input.sort || 'createdAt'] || '').localeCompare(
          String(b[input.sort || 'createdAt'] || ''),
        ) * (input.descending ? -1 : 1),
    );
  return paginate(rows, input, ctx.revision);
}
export const workDomain: DomainModule = {
  namespace: 'work',
  version: 1,
  resource: 'workspace',
  pages: [
    {
      id: 'W02',
      path: '/task',
      resource: 'task',
      parentPath: '/workspace',
      returnTo: '/workspace',
    },
    {
      id: 'W03',
      path: '/message',
      resource: 'message',
      parentPath: '/workspace',
      returnTo: '/workspace',
    },
    {
      id: 'W04',
      path: '/search',
      resource: 'search',
      parentPath: '/workspace',
      returnTo: '/workspace',
    },
  ],
  migrations: [
    {
      version: 1,
      up(state) {
        state.domainData ||= {};
        state.domainData.work ||= {};
      },
    },
  ],
  query: workQuery,
  search: workDocuments,
  suggestions: (ctx) =>
    (['recording', 'batch', 'case', 'plan', 'followUp'] as Kind[]).flatMap(
      (kind) =>
        ctx.list(kind).flatMap((row) => {
          const advice = recommendation(row, ctx.now);
          return advice
            ? [
                {
                  id: `work:${row.uuid}:${row.revision}`,
                  label: advice.action,
                  sourceUuid: row.uuid,
                  action: 'workspaceTask',
                },
              ]
            : [];
        }),
    ),
  commands: {
    accept: {
      label: '任务已接受',
      roles,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        actor(ctx, row);
        transition(row, ['pending']);
        requireDependencies(ctx, row);
        row.status = 'inProgress';
        record(ctx, row, '任务已接受');
        return row;
      },
    },
    transfer: {
      label: '任务已转派',
      roles,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        actor(ctx, row);
        if (!unfinished(row))
          throw new DomainFault('INVALID_TRANSITION', '已结束任务不能转派');
        const owner = ctx
          .members()
          .find(
            (e) =>
              e.memberUuid === input.memberUuid &&
              e.campus === row.campus &&
              e.status === 'active',
          );
        if (!owner)
          throw new DomainFault('VALIDATION', '请选择同校区在职负责人');
        row.owner = owner.memberUuid;
        row.dueAt = need(input.dueAt, '任务期限');
        if (
          !Number.isFinite(Date.parse(row.dueAt)) ||
          Date.parse(row.dueAt) <= Date.parse(ctx.now)
        )
          throw new DomainFault('VALIDATION', '任务期限必须晚于业务时间');
        row.status = 'pending';
        row.note = need(input.note, '转派原因');
        record(ctx, row, '任务已转派');
        return row;
      },
    },
    submit: {
      label: '任务结果已提交',
      roles,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        actor(ctx, row);
        transition(row, ['inProgress']);
        requireDependencies(ctx, row);
        row.result = need(input.note, '处理结果');
        row.status = 'pendingAcceptance';
        record(ctx, row, '任务结果待验收');
        return row;
      },
    },
    approve: {
      label: '任务已验收',
      roles: managers,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        transition(row, ['pendingAcceptance']);
        need(row.result, '处理结果');
        requireDependencies(ctx, row);
        row.note = need(input.note, '验收意见');
        row.status = 'completed';
        row.endAt = ctx.now;
        row.fields = { ...row.fields, acceptedBy: ctx.identity.memberUuid };
        record(ctx, row, '任务已验收完成');
        return row;
      },
    },
    return: {
      label: '任务已退回',
      roles: managers,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        transition(row, ['pendingAcceptance']);
        row.note = need(input.note, '退回原因');
        row.status = 'inProgress';
        record(ctx, row, '任务退回补充');
        return row;
      },
    },
    dependencies: {
      label: '前置任务已调整',
      roles: managers,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        transition(row, ['pending']);
        const requested = dependencies({
          ...row,
          fields: { dependsOn: input.fields?.dependsOn || '[]' },
        });
        const visit = (id: string, seen: Set<string>): void => {
          if (id === row.uuid || seen.has(id))
            throw new DomainFault('VALIDATION', '任务依赖不能形成循环');
          const dep = task(ctx, id);
          if (dep.campus !== row.campus)
            throw new DomainFault('VALIDATION', '请选择同校区前置任务');
          for (const next of dependencies(dep))
            visit(next, new Set([...seen, id]));
        };
        for (const id of requested) visit(id, new Set());
        row.fields = { ...row.fields, dependsOn: JSON.stringify(requested) };
        ctx.touch(row);
        return row;
      },
    },
    coordinate: {
      label: '任务协调已发起',
      roles,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        actor(ctx, row);
        const blocked = dependencies(row).filter((id) => {
          const dep = ctx.get(id);
          return !dep || unfinished(dep);
        });
        if (!blocked.length)
          throw new DomainFault(
            'INVALID_TRANSITION',
            '前置任务均已完成，无需协调',
          );
        const key = blocked
          .map((id) => `${id}:${ctx.get(id)?.revision || 0}`)
          .join('|');
        if (row.fields?.coordinationKey !== key) {
          row.fields = { ...row.fields, coordinationKey: key };
          record(ctx, row, '请协调前置任务，当前任务受阻');
        }
        return row;
      },
    },
    remind: {
      label: '任务已催办',
      roles,
      kind: 'task',
      execute(ctx, input) {
        const row = task(ctx, input.uuid);
        actor(ctx, row);
        if (!unfinished(row))
          throw new DomainFault('INVALID_TRANSITION', '已结束任务无需催办');
        const key = ctx.now.slice(0, 10);
        if (row.fields?.remindedOn !== key) {
          row.fields = { ...row.fields, remindedOn: key };
          record(ctx, row, '任务进展需要确认');
        }
        return row;
      },
    },
    read: {
      label: '消息已读',
      roles,
      kind: 'message',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (!row || row.kind !== 'message')
          throw new DomainFault('FORBIDDEN', '消息不可访问');
        row.status = 'read';
        ctx.touch(row);
        return row;
      },
    },
    acknowledge: {
      label: '消息已确认知悉',
      roles,
      kind: 'message',
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (!row || row.kind !== 'message')
          throw new DomainFault('FORBIDDEN', '消息不可访问');
        row.status = 'read';
        row.fields = {
          ...row.fields,
          acknowledgedAt: ctx.now,
          acknowledgedBy: ctx.identity.memberUuid,
        };
        ctx.touch(row);
        return row;
      },
    },
    subscribe: {
      label: '消息订阅已更新',
      roles,
      execute(ctx, input) {
        const category = need(input.fields?.category, '消息类别');
        if (!['action', 'update', 'alert'].includes(category))
          throw new DomainFault('VALIDATION', '消息类别无效');
        const key = `subscriptions:${contextKey(ctx.context)}`;
        const data = { ...ctx.data() };
        const prefs =
          typeof data[key] === 'object' &&
          !Array.isArray(data[key]) &&
          data[key] !== null
            ? (data[key] as Record<string, boolean>)
            : {};
        data[key] = { ...prefs, [category]: input.fields?.enabled === 'true' };
        ctx.setData(data);
        return undefined;
      },
    },
    pin: {
      label: '证据已加入证据夹',
      roles,
      execute(ctx, input) {
        const row = ctx.get(input.uuid);
        if (!row || !input.evidence || row.kind !== 'recording')
          throw new DomainFault('FORBIDDEN', '证据不可访问');
        const version = row.versions?.find(
          (v) => v.uuid === input.evidence?.transcriptVersionUuid,
        );
        if (
          !version ||
          input.evidence.startMs < 0 ||
          input.evidence.endMs <= input.evidence.startMs ||
          input.evidence.endMs > (version.segments.at(-1)?.endMs || 0)
        )
          throw new DomainFault('VALIDATION', '证据版本或片段无效');
        const key = `evidence:${contextKey(ctx.context)}`;
        const pins = Array.isArray(ctx.data()[key])
          ? (ctx.data()[key] as string[])
          : [];
        const pin = JSON.stringify({
          recordingUuid: row.uuid,
          transcriptVersionUuid: version.uuid,
          startMs: input.evidence.startMs,
          endMs: input.evidence.endMs,
        });
        ctx.setData({ ...ctx.data(), [key]: [...new Set([...pins, pin])] });
        return row;
      },
    },
    unpin: {
      label: '证据已移出证据夹',
      roles,
      execute(ctx, input) {
        const key = `evidence:${contextKey(ctx.context)}`;
        const pins = Array.isArray(ctx.data()[key])
          ? (ctx.data()[key] as string[])
          : [];
        ctx.setData({
          ...ctx.data(),
          [key]: pins.filter((e) => e !== input.fields?.pinKey),
        });
        return undefined;
      },
    },
  },
};
